import React, { useEffect, useState, useMemo } from 'react';

import {
    Label,
    TwoThirdsLayout,
    PageWrapper,
    Panel,
    Flex,
    Button,
    useNotification,
    Divider,
    Select,
    Span,
    Toggle,
    Heading3
} from '@ubisend/pulse-components';
import { useQuery, useMutation, useQueryClient } from '@ubisend/pulse-hooks';

import { updateEmailPasswordSettings } from '../../../api/index';

const PASSWORD_EXPIRATION_OPTIONS = [
    { label: '90 Days (Default)', value: 90 },
    { label: '180 Days', value: 180 },
    { label: '365 Days', value: 365 },
    { label: 'No Expiration', value: 999 }
];

const EmailPasswordSettings = () => {
    const [passwordExpirationEnabled, setPasswordExpirationEnabled] = useState(false);
    const [passwordExpiration, setPasswordExpiration] = useState(90);

    const { showSuccess } = useNotification();
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['owner/auth/email/settings']
    });

    const mutation = useMutation({
        mutationFn: updateEmailPasswordSettings,
        onSuccess: () => {
            showSuccess('Successfully updated Email Password settings');
            queryClient.invalidateQueries({
                queryKey: ['owner/auth/email/settings']
            });
        }
    });

    useEffect(() => {
        if (!query.isSuccess || !query.data?.data) {
            return;
        }

        const {
            password_expiration_enabled = false,
            password_expiration = 90
        } = query.data.data;

        setPasswordExpirationEnabled(Boolean(password_expiration_enabled));
        setPasswordExpiration(password_expiration);
    }, [query.isSuccess, query.data]);

    const handlePasswordExpirationToggle = () => {
        const toggled = !passwordExpirationEnabled;
        setPasswordExpirationEnabled(toggled);

        if (!toggled) {
            setPasswordExpiration(90);
        }
    };

    const handlePasswordExpirationChange = option => {
        setPasswordExpiration(option.value);
    };

    const handleSubmit = event => {
        event.preventDefault();

        mutation.mutate({
            password_expiration_enabled: passwordExpirationEnabled,
            password_expiration: passwordExpirationEnabled
                ? passwordExpiration
                : 90
        });
    };

    const isDirty = useMemo(() => {
        if (!query.isSuccess || !query.data?.data) {
            return false;
        }

        const originalEnabled =
            Boolean(query.data.data.password_expiration_enabled);

        const originalExpiration =
            query.data.data.password_expiration ?? 90;

        const currentExpiration = passwordExpirationEnabled
            ? passwordExpiration
            : 90;

        return (
            originalEnabled !== passwordExpirationEnabled ||
            originalExpiration !== currentExpiration
        );
    }, [
        query.isSuccess,
        query.data,
        passwordExpirationEnabled,
        passwordExpiration
    ]);

    const selectedExpirationOption = useMemo(
        () =>
            PASSWORD_EXPIRATION_OPTIONS.find(
                o => o.value === passwordExpiration
            ),
        [passwordExpiration]
    );

    return (
        <PageWrapper
            header="Email Password Settings"
            subheader="Configure your email/password authentication settings"
        >
            <TwoThirdsLayout>
                <Panel loading={query.isLoading}>
                    {query.isSuccess && (
                        <form onSubmit={handleSubmit}>
                            <Flex col ySpace>
                                <Flex center between>
                                    <Flex col>
                                        <Heading3>Password Expiration</Heading3>
                                        <Span>
                                            Enforce password rotation for users authenticating with email and password.
                                        </Span>
                                    </Flex>

                                    <Toggle
                                        title="password-expiration"
                                        on={passwordExpirationEnabled}
                                        handleToggleChange={handlePasswordExpirationToggle}
                                    />
                                </Flex>

                                <Flex col ySpace>
                                    <Label htmlFor="password_expiration_period">Password expiration period</Label>
                                    <Select
                                        id="password_expiration_period"
                                        name="password_expiration_period"
                                        options={PASSWORD_EXPIRATION_OPTIONS}
                                        value={selectedExpirationOption}
                                        onChange={handlePasswordExpirationChange}
                                        isDisabled={!passwordExpirationEnabled}
                                    />
                                </Flex>

                                <Divider fullWidth />

                                <Flex right>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        loading={mutation.isPending}
                                        disabled={!isDirty}
                                    >
                                        Save
                                    </Button>
                                </Flex>
                            </Flex>
                        </form>
                    )}
                </Panel>
            </TwoThirdsLayout>
        </PageWrapper>
    );
};

export default EmailPasswordSettings;